import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Skeleton,
  Link
} from '@mui/material';
import { Download, OpenInNew, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { colors } from '../../themes/typography';

/**
 * Modal component for displaying QR codes with download functionality
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onClose - Callback when modal closes
 * @param {Object} props.qrData - QR code data from API
 * @param {string} props.qrData.imageUrl - URL of QR code image
 * @param {string} props.qrData.promotionUrl - URL to promotions page
 * @param {string} props.machineId - Machine ID for display and filename
 */
const QrCodeModal = ({ open, onClose, qrData, machineId }) => {
  const { t } = useTranslation();
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  // Handle image load completion
  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  // Handle image load error
  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  // Handle QR code download
  const handleDownload = async () => {
    if (!qrData?.imageUrl || imageError) return;

    try {
      // Fetch image as blob
      const response = await fetch(qrData.imageUrl);
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `qr_machine_${machineId}.png`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download QR code:', error);
      // Could add toast notification here for user feedback
    }
  };

  // Handle promotion link click
  const handlePromotionClick = () => {
    if (qrData?.promotionUrl) {
      window.open(qrData.promotionUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (open) {
      setImageLoading(true);
      setImageError(false);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          p: '8px'
        }
      }}
    >
      <DialogTitle sx={{
        fontSize: '18px',
        fontWeight: 700,
        color: colors.text.primary,
        pb: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <span>{t('machineQrCode', { machineId }) || `Machine QR Code - ${machineId}`}</span>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: colors.text.secondary }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: '16px' }}>
        {/* QR Code Display */}
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          {/* QR Image Container */}
          <Box sx={{
            position: 'relative',
            width: '300px',
            height: '300px',
            backgroundColor: colors.background.subtle,
            borderRadius: '8px',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {imageLoading && (
              <Skeleton 
                variant="rectangular" 
                width={300} 
                height={300}
                sx={{ borderRadius: '8px' }}
              />
            )}
            
            {!imageError && qrData?.imageUrl && (
              <img
                src={qrData.imageUrl}
                alt={`QR Code for machine ${machineId}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: imageLoading ? 'none' : 'block'
                }}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            )}
            
            {imageError && (
              <Typography sx={{
                color: colors.text.secondary,
                fontSize: '14px',
                textAlign: 'center',
                p: '16px'
              }}>
                {t('qrLoadError', 'Failed to load QR code')}
              </Typography>
            )}
          </Box>

          {/* Info Text */}
          <Typography sx={{
            fontSize: '14px',
            color: colors.text.secondary,
            textAlign: 'center'
          }}>
            {t('qrCodeDescription', 'Scan this QR code to view promotions for this machine')}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        px: '24px', 
        pb: '16px',
        gap: '8px',
        justifyContent: 'center'
      }}>
        {/* Download Button */}
        <Button
          variant="contained"
          onClick={handleDownload}
          disabled={!qrData?.imageUrl || imageError || imageLoading}
          startIcon={<Download />}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            backgroundColor: colors.action.primary,
            '&:hover': {
              backgroundColor: colors.action.primaryHover
            }
          }}
        >
          {t('download', 'Download')}
        </Button>

        {/* View Promotions Button */}
        <Button
          variant="text"
          onClick={handlePromotionClick}
          disabled={!qrData?.promotionUrl}
          endIcon={<OpenInNew sx={{ fontSize: '16px' }} />}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            color: colors.action.primary
          }}
        >
          {t('viewPromotions', 'View Promotions')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QrCodeModal;